import {Component, forwardRef, Input, TemplateRef, ViewChild} from '@angular/core';
import {ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR} from "@angular/forms";
import {
  MemberAssignment,
  memberAssignmentColDefs,
  MemberAssignmentForm,
  PhaseAssignmentForm,
  ProjectAssignmentStatus
} from "../../interfaces/memberAssignment";
import {SearchComponent} from "../../search/search.component";
import {Searchable, User, UserResult} from "../../interfaces/user";
import {UserDialogComponent} from "../../dialogs/popup-dialog/user-dialog.component";
import {Subject} from "rxjs";
import {EditDialogComponent, EditType} from "../../dialogs/edit-dialog/edit-dialog.component";
import {Globals} from "../../shared/Globals";
import {SubscribedComponent} from "../../subscribed.component";
import {NbDialogService} from "@nebular/theme";
import {TranslateService} from "@ngx-translate/core";
import {Phase, Project} from "../../interfaces/project";
import {ProjectRole} from "../../interfaces/projectRole";
import {DirectoryService} from "../../shared/directory.service";
import {BSValidators} from "../../shared/bsvalidators";
import {ColDef, ColType, ListEntry} from "../../interfaces/colDef";
import {NotifierService} from "../../shared/notifier.service";
import {AuthService} from "../../shared/auth.service";
import {ProjectService} from "../../shared/project.service";
import {TimeTrackingService} from "../../shared/time-tracking.service";
import {DMenuItem} from "../../dialogs/menu-dialog/menu-dialog.component";
import {Router} from "@angular/router";
import {UsersService} from "../../shared/users.service";

@Component({
    selector: 'bst-user-project-assigner',
    templateUrl: './user-project-assigner.component.html',
    styleUrls: ['./user-project-assigner.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UserProjectAssignerComponent),
            multi: true
        }
    ],
    standalone: false
})
export class UserProjectAssignerComponent extends SubscribedComponent implements ControlValueAccessor {

  @ViewChild('userResultHeaderTemplate', {static: true}) userResultHeaderTemplate: TemplateRef<any>;
  @ViewChild('userResultBodyTemplate', {static: true}) userResultBodyTemplate: TemplateRef<any>;
  @ViewChild('userSelectionHeaderTemplate', {static: true}) userSelectionHeaderTemplate: TemplateRef<any>;
  @ViewChild('userSelectionBodyTemplate', {static: true}) userSelectionBodyTemplate: TemplateRef<any>;
  @ViewChild('userSummaryTemplate', {static: true}) userSummaryTemplate: TemplateRef<any>;
  @ViewChild('userButtonTemplate', {static: true}) userButtonTemplate: TemplateRef<any>;

  @Input() range: {start: Date, end: Date} = null;
  @Input() project: Project = null;
  @Input() projectroles: ProjectRole[];
  public disabled: boolean = false;
  public Globals = Globals;
  public readonly scores = [0.2, 0.4, 0.6, 0.73, 0.87];

  public phases: Phase[] = [];
  // public assignments: MemberAssignment[] = null;
  public newassignments: MemberAssignment[] = [];
  ProjectAssignmentStatus = ProjectAssignmentStatus;

  phaseMenu: DMenuItem<Phase>[] = [
    {
      title: 'tracking.start',
      action: (phase, m, i) => this.startTrack(phase),
      icon: 'play-circle',
      status: 'primary',
      hidden: phase => !this.canTrack(phase)
    },
    {
      title: 'edit',
      action: phase => this.openPhaser(phase),
      icon: 'edit',
      status: 'primary',
      hidden: () => this.disabled
    },
    {
      title: 'delete',
      action: phase => this.deletePhase(phase),
      status: 'danger',
      icon: 'trash-2',
      hidden: () => this.disabled || this.phases?.length <= 1
    },
    {
      title: 'changephase',
      action: phase => this.movePhase(phase),
      icon: 'corner-up-right',
      hidden: () => this.disabled || this.phases?.length <= 1
    }
  ];

  // Function to call when the value changes.
  onChange = (value: Phase[]) => {};
  // Function to call when the input is touched (when a star is clicked).
  onTouched = () => {};

  constructor(private dialogService: NbDialogService,
              private directoryService: DirectoryService,
              private fb: FormBuilder,
              private notifier: NotifierService,
              private auth: AuthService,
              private tracking: TimeTrackingService,
              private router: Router,
              private translate: TranslateService,
              private usersService: UsersService) {
    super();
  }

  public static async assignmentDialog(directoryService: DirectoryService,
                                        dialogService: NbDialogService,
                                        projectService: ProjectService,
                                        user: User,
                                        timeline:{start: Date|string, end: Date|string} = null,
                                        hours: number = null,
                                        loading = null){
    const pdef: ColDef = memberAssignmentColDefs.find(o => o.name === 'Phase_id');
    pdef.list = [];

    const projectroles = await directoryService.getProjectRoles();
    const def: ColDef = memberAssignmentColDefs.find((o) => o.name === 'ProjectRole_id' );
    def.list = projectroles.map((o) => {
      const d: ListEntry = {
        id: o.id,
        name: o.name
      };
      return d;
    });

    let defs: ColDef[] = [
      {
        name: 'project',
        title: 'Project',
        required: true,
        length: 1,
        type: ColType.LISTADDER
      },
    ];
    defs = defs.concat(memberAssignmentColDefs);

    let lastProjectId = null;
    const result = await new Promise<MemberAssignment|null>((resolve, reject) => {
      const sub = dialogService.open<EditDialogComponent<MemberAssignmentForm>>(EditDialogComponent, {
        autoFocus: true,
        context: {
          type: EditType.ADD,
          title: 'Project assignment',
          subtitle: user.name_first + ' ' + user.name_last,
          description: defs,
          data: {
            dedication: hours,
            timeline: timeline ? {
              start: Globals.fromDateString(timeline.start),
              end: Globals.fromDateString(timeline.end)
            } : null,
            cost: user.user_to_client?.cost,
            offered: user.user_to_client?.offered,
            ProjectRole_id: null,
            Phase_id: null,
            description: null,
            status: ProjectAssignmentStatus.ACTIVE
          },
          formChanged: async (value, form, editable) => {
            if(form.touched){
              let ph = null;
              const pf = form.get('Phase_id');
              const opts = {emitEvent: false};
              const id = value.project?.id;
              if(id && id !== lastProjectId){
                // Get Phases for this project
                pf.disable(opts);
                const phases = await projectService.getPhases(id).catch(() => null);
                ph = phases?.map((p) => {
                  return {
                    id: p.id,
                    name: p.name
                  };
                }) || [];
                if(pdef.list.length){
                  pf.setValue(pdef.list[pdef.list.length-1].id, opts);
                }
                pf.enable(opts);
                lastProjectId = id;
              }
              else if(!id){
                ph = [];
                lastProjectId = null;
              }

              if(ph !== null){
                pdef.list = ph;
                pf.setValue(null, opts);
              }
            }
          }
        }
      }).onClose.subscribe(async (choice: MemberAssignmentForm) => {
        let assignment = null;
        if(choice){
          assignment = {
            id: 0,
            User_id: user.id,
            Phase_id: choice.Phase_id,
            start_on: Globals.toISODate(choice.timeline.start),
            end_on: Globals.toISODate(choice.timeline.end),
            dedication: choice.dedication,
            cost: choice.cost,
            offered: choice.offered,
            status: choice.status,
            description: choice.description,
            ProjectRole_id: choice.ProjectRole_id
          };
        }

        if(sub){
          sub.unsubscribe();
        }

        return resolve(assignment);
      });
    });

    if(result){
      loading?.next(true);
      const assigned = await projectService.addAssignment(result);
      loading?.next(false);
      return assigned;
    }
    return null;
  }

  canTrack(phase: Phase): boolean{
    return !this.tracking.isTracking && !!phase.members.find(o => this.auth.isMe(o.user?.id));
  }

  get canEdit(): boolean{
    return this.auth.isAdmin || this.auth.isStaffer;
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(value: Phase[]): void {
    this.phases = Array.from(value || []);
    // this.onChange(this.assignments);
  }

  openDetails(item: UserResult, params: any){
    item.open = !item.open;
    if(item.open){
      item.form = this.fb.group({
        Phase_id: [this.phases?.length ? this.phases[this.phases.length-1].id : null, []],
        ProjectRole_id: [null, [BSValidators.required]],
        offered: [item.user.user_to_client?.offered || 0.00, [BSValidators.required]],
        cost: [item.user.user_to_client?.cost || 0.00, [BSValidators.required]],
        time: [params.time, [BSValidators.required]],
        capacity: [params.capacity ?? 0, [BSValidators.required]]
      });
    }
    else{
      delete item.form;
    }
  }

  async openPhaser(foredit: Phase|null = null) {
    const trans: any = await this.translate.get(['name', 'description', 'projectphase']).toPromise();
    const coldefs: ColDef[] = [
      {
        name: 'name',
        title: trans.name,
        type: ColType.STRING,
        required: true,
        length: 100
      },
      {
        name: 'description',
        title: trans.description,
        type: ColType.AREA,
        length: 500,
        required: false
      }
    ];

    this.subs.push(this.dialogService.open<EditDialogComponent<Phase>>(EditDialogComponent, {
      autoFocus: true,
      context: {
        title: trans.projectphase,
        description: coldefs,
        type: foredit ? EditType.EDIT : EditType.ADD,
        data: foredit ?? {
          id: null,
          name: 'Phase ' + (this.phases?.length + 1),
        }
      }
    }).onClose.subscribe(async choice => {
      if(choice){
        if(foredit){
          foredit.name = choice.name;
          foredit.description = choice.description || null;
        }
        else {
          const newid = -(new Date()).getTime();
          this.phases.push({
            id: newid,
            name: choice.name,
            description: choice.description || null,
            members: [],
            Project_id: this.project?.id
          });
        }

        this.onChange(this.phases);
      }
    }));
  }

  openAssigner() {
    // const cap = this.range ? Globals.calculateBusinessDays(this.range.start, this.range.end) : 5;
    this.newassignments = [];

    this.subs.push(this.dialogService.open<SearchComponent<UserResult>>(SearchComponent, {
      context: {
        autosearch: true,
        target: 'users',
        hidden: [],
        options: {
          capacity: null /*(cap * Globals.DAY_HOURS)*/,
          time: this.range
        },
        headerTemplate: this.userResultHeaderTemplate,
        bodyTemplate: this.userResultBodyTemplate,
        selectionHeaderTemplate: this.userSelectionHeaderTemplate,
        selectionBodyTemplate: this.userSelectionBodyTemplate,
        summaryTemplate: this.userSummaryTemplate,
        buttonTemplate: this.userButtonTemplate
      }
    }).onClose.subscribe(async (response) => {
      if(response !== false) {
        this.newassignments.forEach((ass) => {
          if(response.data?.invite && ass.status !== ProjectAssignmentStatus.INACTIVE) {
            // Set everyone to pending status, will send invite email on backend
            ass.status = ProjectAssignmentStatus.PENDING;
          }

          for(let i=0; i<this.phases.length; i++){
            const p = this.phases[i];
            if(p.id === ass.Phase_id){
              if(!p.members){
                p.members = [];
              }
              p.members.push(ass);
              break;
            }
          }
        });

        // this.assignments = (this.assignments || []).concat(this.newassignments);
        this.newassignments = [];
        this.onChange(this.phases);
      }
    }));
  }

  previewUser(user: User) {
    this.subs.push(this.dialogService.open<UserDialogComponent>(UserDialogComponent, {
      context: {
        componentContext: {
          couldEdit: false,
          id: user.id
        }
      }
    }).onClose.subscribe(async something => {

    }));
  }

  async removeAssignment(assignment: MemberAssignment){
    if(this.phases) {
      if(await this.notifier.confirm('assignmentdelete',
        assignment?.user?.name_first + ' ' + assignment?.user?.name_last,
        'deleteforever', 'danger')){

        for(let i=0; i<this.phases.length; i++){
          const members = this.phases[i].members || [];
          const index = members.findIndex((o) => {
            return o.id === assignment.id;
          });

          if(index !== -1){
            this.phases[i].members.splice(index, 1);
            this.onChange(this.phases);
            break;
          }
        }
      }
    }
  }

  setAssignment(userResult: UserResult, params: any, select: Subject<UserResult>){
    const index = this.newassignments.findIndex((o) => {
      return o.user.id === userResult.user.id;
    });

    if(userResult.form) {
      if (index === -1) {
        this.newassignments.push({
          user: userResult.user,
          dedication: parseInt(userResult.form.get('capacity').value) ?? 0,
          project: this.project,
          status: ProjectAssignmentStatus.ACTIVE,
          start_on: params.time.start,
          end_on: params.time.end,
          project_role: userResult.form.get('ProjectRole_id').value,
          Phase_id: userResult.form.get('Phase_id').value,
          offered: userResult.form.get('offered').value,
          cost: userResult.form.get('cost').value
        });
        userResult.open = false;
      } else {
        this.newassignments.splice(index, 1);
      }

      select.next(userResult);
    }
  }

  priceToClient(item: UserResult, params: any, ass: MemberAssignment = null){
    if(ass){
      return ass.offered * (ass.dedication || 0);
    }
    else if(item && item.form){
      const offered = item.form.get('offered');
      const capacity = item.form.get('capacity');
      return offered.value * (capacity.value || 0);
    }
    return null;
  }

  margin(item: UserResult, params: any, ass: MemberAssignment = null){
    const total = this.totalCosts(item, params, ass);
    const price = this.priceToClient(item, params, ass);
    if(total !== null && price !== null && price > 0){
      return 1 - (total / price);
    }
    return null;
  }

  totalCosts(item: UserResult, params: any, ass: MemberAssignment = null){
    if(ass){
      return ass.cost * (ass.dedication || 0);
    }
    else if(item && item.form){
      const cost = item.form.get('cost');
      const capacity = item.form.get('capacity');
      return cost.value * (capacity.value || 0);
    }
    return null;
  }

  assignmentForUser(user: User){
    return this.newassignments.find((o) => {
      return o.user && o.user.id === user.id;
    });
  }

  phaseForAssignment(ass: MemberAssignment){
    return this.phases?.find((o) => {
      return ass.Phase_id === o.id;
    });
  }

  editAssignment(assignment: MemberAssignment, emitevent: boolean = true) {
    const def: ColDef = memberAssignmentColDefs.find((o) => o.name === 'ProjectRole_id');
    def.list = this.projectroles.map((o) => {
      const d: ListEntry = {
        id: o.id,
        name: o.name
      };
      return d;
    });

    const pdef: ColDef = memberAssignmentColDefs.find((o) => o.name === 'Phase_id');
    pdef.list = this.phases.map((o) => {
      const d: ListEntry = {
        id: o.id,
        name: o.name
      };
      return d;
    });

    const sub = this.dialogService.open<EditDialogComponent<MemberAssignmentForm>>(EditDialogComponent, {
      autoFocus: true,
      context: {
        title: this.translate.instant('assignment'),
        subtitle: assignment.user ? assignment.user.name_first + ' ' + assignment.user.name_last : null,
        description: memberAssignmentColDefs,
        data: {
          dedication: assignment.dedication,
          timeline: {
            start: Globals.fromDateString(assignment.start_on),
            end: Globals.fromDateString(assignment.end_on)
          },
          description: assignment.description || null,
          cost: assignment.cost,
          offered: assignment.offered,
          ProjectRole_id: assignment.project_role ? assignment.project_role.id : null,
          Phase_id: assignment.Phase_id || null,
          status: assignment.status
        }
      }
    }).onClose.subscribe(async (choice: MemberAssignmentForm) => {
      sub.unsubscribe();
      if(choice){
        this.movetoPhase(assignment, choice.Phase_id);
        assignment.start_on = choice.timeline.start;
        assignment.end_on = choice.timeline.end;
        assignment.status = choice.status;
        assignment.dedication = choice.dedication;
        assignment.cost = choice.cost;
        assignment.offered = choice.offered;
        assignment.description = choice.description;
        assignment.project_role = this.projectroles.find((o) => o.id === choice.ProjectRole_id);

        if(emitevent) {
          this.onChange(this.phases);
        }
      }
    });
  }

  totalHours(assignments: MemberAssignment[]): number{
    if(assignments){
      return assignments.reduce((p, c) => p + (c.status !== ProjectAssignmentStatus.INACTIVE ? c.dedication || 0 : 0), 0);
    }
    return null;
  }

  totalPriceToClient(data: UserResult[], params: any, asses: MemberAssignment[] = null): number {
    let total: number = 0;
    if(asses){
      total = asses.reduce((p, c) => p + (c.status !== ProjectAssignmentStatus.INACTIVE ? this.priceToClient(null, params, c) || 0 : 0), 0);
    }
    else if(data){
      for (let d of data) {
        const ass = this.assignmentForUser(d.user);
        if (ass && ass.status !== ProjectAssignmentStatus.INACTIVE) {
          total += this.priceToClient(d, params, ass);
        }
      }
    }
    return total;
  }

  totalTotalCosts(data: UserResult[], params: any, asses: MemberAssignment[] = null) {
    let total: number = 0;
    if(asses){
      total = asses.reduce((p, c) => p + (c.status !== ProjectAssignmentStatus.INACTIVE ? this.totalCosts(null, params, c) || 0 : 0), 0);
    }
    else if(data){
      for (let d of data) {
        const ass = this.assignmentForUser(d.user);
        if (ass && ass.status !== ProjectAssignmentStatus.INACTIVE) {
          total += this.totalCosts(d, params, ass);
        }
      }
    }
    return total;
  }

  totalMargin(data: UserResult[], params: any, asses: MemberAssignment[] = null) {
    const total = this.totalTotalCosts(data, params, asses);
    const price = this.totalPriceToClient(data, params, asses);
    if(total !== null && price !== null && price > 0){
      return 1 - (total / price);
    }
    return null;
  }

  logo(user: User) {
    return user.logo && user.logo.preview ? Globals.storage(user.logo.preview) : user.provider_avatar;
  }

  async deletePhase(phase: Phase) {
    const index = this.phases.indexOf(phase);
    const wantsto = await this.notifier.confirm('deletephase', phase.name, 'delete', 'danger');
    if(wantsto && index !== -1){
      this.phases.splice(index, 1);
      this.onChange(this.phases);
    }
  }

  movePhase(phase: Phase, emitevent: boolean = true) {

    let last = null;
    const cols: ColDef[] = [{
      name: 'Phase_id',
      title: 'Move assignments to',
      list: this.phases.filter(o => o.id !== phase.id).map((o) => {
        last = o.id;
        const d: ListEntry = {
          id: o.id,
          name: o.name
        };
        return d;
      }),
      required: true,
      type: ColType.SELECT
    }];

    const sub = this.dialogService.open<EditDialogComponent<PhaseAssignmentForm>>(EditDialogComponent, {
      autoFocus: true,
      context: {
        title: this.translate.instant('projectphase'),
        subtitle: phase.name,
        description: cols,
        data: {
          Phase_id: last
        }
      }
    }).onClose.subscribe((choice: PhaseAssignmentForm) => {
      sub.unsubscribe();
      if(choice && choice.Phase_id && phase.members?.length){
        phase.members.forEach((member) => {
          this.movetoPhase(member, choice.Phase_id);
        });

        if(emitevent){
          this.onChange(this.phases);
        }
      }
    });
  }

  private movetoPhase(member: MemberAssignment, newid){
    member.Phase_id = newid;
    for(let i=0; i<this.phases.length; i++){
      const p = this.phases[i];
      const index = p.members.indexOf(member);
      if(index !== -1){
        p.members.splice(index, 1);
        for(let j=0; j<this.phases.length; j++){
          const p2 = this.phases[j];
          if(p2.id === member.Phase_id){
            p2.members.push(member);
            break;
          }
        }
        break;
      }
    }
  }

  overlapsToday(phase: Phase): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return !!(phase.members?.find((o) => {
      return Globals.fromDateString(o.start_on)?.getTime() <= today.getTime()
        && today.getTime() <= Globals.fromDateString(o.end_on)?.getTime();
    }));
  }

  async startTrack(phase: Phase) {
    const user_to_phase = phase.members.find(o => this.auth.isMe(o.user?.id));
    if(user_to_phase && !this.isLoading && !this.tracking.isTracking){
      this.loading.next(true);
      // go to tracking page
      const did = await this.tracking.start({
        User_to_Phase_id: user_to_phase.id
      });

      // then start
      if(did) {
        await this.router.navigate(['/registration/hours/tracker']);
      }
      this.loading.next(false);
    }
  }

  dr(data: any) {
    return (data.delta ?? 0) - (data.form?.get('capacity').value ?? 0);
  }

  /**
   * Pre-select a consultant in the assigner after a CRM convert-to-project
   * handoff. Fetches the user by id, checks they are not already assigned,
   * then adds them to the last phase with default values (0 hours, project
   * date range, no role) exactly the way closing the search dialog does
   * (see openAssigner onClose). The project-component's assignmentForm
   * valueChanges listener persists the change immediately so the staffer
   * sees the assignment row and can open it to fill in role/hours/dates.
   *
   * No-ops when: userId is falsy, no phases exist, or the user is already
   * assigned to any phase.
   */
  async preselectUser(userId: number): Promise<void> {
    if (!userId || !this.phases?.length) { return; }

    // No-op if already assigned.
    const alreadyAssigned = this.phases.some(p =>
      p.members?.some(m => m.user?.id === userId)
    );
    if (alreadyAssigned) { return; }

    let user: User;
    try {
      user = await this.usersService.getUser(userId);
    } catch (e) {
      console.warn('preselectUser: could not fetch user', userId, e);
      return;
    }
    if (!user) { return; }

    const targetPhase = this.phases[this.phases.length - 1];
    const assignment: MemberAssignment = {
      user,
      dedication: 0,
      project: this.project,
      status: ProjectAssignmentStatus.ACTIVE,
      start_on: this.range?.start ?? null,
      end_on: this.range?.end ?? null,
      project_role: null,
      Phase_id: targetPhase.id as number,
      offered: user.user_to_client?.offered ?? 0,
      cost: user.user_to_client?.cost ?? 0,
    };

    if (!targetPhase.members) { targetPhase.members = []; }
    targetPhase.members.push(assignment);
    this.onChange(this.phases);
  }
}
